/**
 * Call Vault routes that own ServiceNow table and OAuth semantics.
 *
 * Common feed operations still own scheduling, synchronization, and deletion.
 * This module sends only the provider-specific table record ceiling and
 * credential bundle required for create/edit workflows.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { connectorPath, previewConnectorFeed } from '../../api'
import type {
  ConnectorFeedMutation,
  ConnectorInitialSync,
  ConnectorSchedule,
} from '../../types'
import type {
  ServiceNowConnectionStatus,
  ServiceNowFeedOptions,
  ServiceNowFilter,
  ServiceNowSelectionPreview,
  ServiceNowTable,
  ServiceNowTableAccessCatalog,
  ServiceNowTableDefinitions,
  ServiceNowWorkspaceCredentials,
} from '../types'
import { serviceNowSourceNames } from '../types'

export interface CreateServiceNowFeedRequest {
  table: ServiceNowTable
  initialSync: ConnectorInitialSync
  schedule: ConnectorSchedule
  sourceDisplayFieldKeys: string[]
  servicenow: ServiceNowFeedOptions
}

export const createServiceNowFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateServiceNowFeedRequest,
) => (await api.post<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds`,
  {
    provider: 'servicenow',
    direction: 'inbound',
    remoteFolderId: request.table,
    remoteFolderName: serviceNowSourceNames[request.table],
    initialSync: request.initialSync,
    schedule: request.schedule,
    sourceDisplayFieldKeys: request.sourceDisplayFieldKeys,
    servicenow: request.servicenow,
  },
)).data

export const previewServiceNowFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateServiceNowFeedRequest,
) => previewConnectorFeed(api, orgId, vaultId, {
  provider: 'servicenow',
  direction: 'inbound',
  remoteFolderId: request.table,
  remoteFolderName: serviceNowSourceNames[request.table],
  initialSync: request.initialSync,
  schedule: request.schedule,
  sourceDisplayFieldKeys: request.sourceDisplayFieldKeys,
  servicenow: request.servicenow,
})

export const updateServiceNowFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  schedule: ConnectorSchedule,
  sourceDisplayFieldKeys: string[],
  servicenow: ServiceNowFeedOptions,
) => (await api.put<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/servicenow`,
  { schedule, sourceDisplayFieldKeys, servicenow },
)).data

export const getServiceNowConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<ServiceNowConnectionStatus>(
  `${connectorPath(orgId, vaultId)}/servicenow/connection`,
)).data

export const getServiceNowTables = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<ServiceNowTableDefinitions>(
  `${connectorPath(orgId, vaultId)}/servicenow/tables`,
)).data

export const getServiceNowTableAccess = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  apiUrl: string,
  credentialSource: ServiceNowFeedOptions['credentialSource'],
) => (await api.get<ServiceNowTableAccessCatalog>(
  `${connectorPath(orgId, vaultId)}/servicenow/table-access?apiUrl=${encodeURIComponent(apiUrl)}&credentialSource=${encodeURIComponent(credentialSource)}`,
)).data

export const getServiceNowSelectionPreview = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: {
    apiUrl: string
    table: ServiceNowTable
    credentialSource: ServiceNowFeedOptions['credentialSource']
    recordLimit: number
    filters: ServiceNowFilter[]
  },
) => (await api.post<ServiceNowSelectionPreview>(
  `${connectorPath(orgId, vaultId)}/servicenow/selection-preview`,
  request,
)).data

export const saveServiceNowConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  credentials: ServiceNowWorkspaceCredentials,
) => api.put(
  `${connectorPath(orgId, vaultId)}/servicenow/connection`,
  credentials,
)

export const deleteServiceNowConnection = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => api.delete(`${connectorPath(orgId, vaultId)}/servicenow/connection`)
